import { BreakPoint } from '@adornis/base/client/frontend-helpers/window-size.js';
import { BuddyUserDetails } from '@adornis/bh-matching-user/components/buddy-user-details.js';
import type { BuddyUser } from '@adornis/bh-matching-user/entities/buddy-user.js';
import { userFieldBlackList } from '@adornis/bh-matching-user/user-field-blacklist.js';
import '@adornis/buchungsheld-theme/fonts/fonts.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { html, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { MentoringMentee, MentoringMentor } from '../../entities/mentoring-user.js';
import { fieldOrderMentee, fieldOrderMentor } from '../form-mapping.js';
import { MNMenteeFormDialog } from './mentoring-form-dialog.js';
import { mentorVisibilityToggle } from './misc.js';

@customElement('mentoring-user-details')
export class MentoringUserDetails extends BuddyUserDetails {
  override _userDetails(onlyFirstThree?: boolean) {
    const user = this.user.value;
    if (!user) return html` <tr-text> 404 - No user found! </tr-text> `;

    const fieldOrder = this.user.value instanceof MentoringMentee ? fieldOrderMentee : fieldOrderMentor;
    const fields = Object.values((this.user.value.constructor as typeof BuddyUser).fields)
      .filter(field => !(this.fieldBlackList ?? userFieldBlackList).includes(field.name))
      .sort((a, b) => fieldOrder.indexOf(a.name) - fieldOrder.indexOf(b.name));
    return html`
      <x-flex flex space="sm">
        <x-grid space="xs" flex columns=${this._size.componentDevice >= BreakPoint.phone ? '1fr 2fr' : '1'}>
          ${repeat(fields.slice(0, onlyFirstThree ? 2 : undefined), field => {
            switch (field.name) {
              case 'preferedMentorsIDs':
                return;
              case 'preferedMentors':
                if (!user[field.name] || user[field.name].length === 0) return;
                return html`
                  <tr-text bold> ${this._translator.translate('field', field.name)} </tr-text>
                  <tr-flex space="xs">
                    ${repeat(
                      user[field.name],
                      (mentor: MentoringMentor) => html`<x-text> ${mentor.firstName} ${mentor.lastName} </x-text>`,
                    )}
                  </tr-flex>
                `;
              case 'profilePicture':
                return html`<tr-text bold> ${this._translator.translate('field', field.name)} </tr-text>
                  <tr-flex>
                    <x-image
                      disabled
                      .value=${user[field.name]}
                      loading="lazy"
                      mode="cover"
                      ${css({ width: '100px', height: '100px', borderRadius: '50%' })}
                    ></x-image>
                  </tr-flex>`;
              case 'curriculumVitae':
                return html` <tr-text bold> ${this._translator.translate('field', field.name)} </tr-text>
                  <tr-button
                    @click=${async () => {
                      const link = await AdornisFile.getDownloadLink(user[field.name]);
                      goTo(link, { target: '_blank' });
                    }}
                  >
                    Download
                  </tr-button>`;
              case 'birthday':
                return html`
                  <tr-text bold> ${this._translator.translate('field', field.name)} </tr-text>
                  <tr-text> ${user[field.name]?.toLocaleDateString() ?? '-'} </tr-text>
                `;
              default:
                return this._userDetailsField(field, user[field.name]);
            }
          })}
        </x-grid>
      </x-flex>
    `;
  }

  override render() {
    return html`
      <x-flex space="md" h100 flex @click=${() => (this._isOpen = true)} ${css({ position: 'relative' })}>
        <x-flex ${css({ position: 'absolute', top: '0px', left: '40px' })}>
          ${this.user.value && this.user.value instanceof MentoringMentor
            ? mentorVisibilityToggle({
                mentor: this.user.value,
                translate: this._translator.translate,
              })
            : nothing}
        </x-flex>

        <tr-button-small
          mode="icon"
          icon="edit"
          ${css({ width: 'min-content' })}
          tone=${this.colors.tone.onyx}
          @click=${async () => {
            const target = this.user.value as MentoringMentor | MentoringMentee;
            if (!target) return;

            MNMenteeFormDialog.showDialog(target);
          }}
        ></tr-button-small>

        ${this._isOpen || this.alwaysOpen
          ? html`
              ${this._userDetails()}
              <x-hr></x-hr>
              ${this._note()}
            `
          : html` ${this._userDetails(true)} `}
        ${this.alwaysOpen || this.hideToggle
          ? nothing
          : html`
              <x-button
                mode="icon"
                icon=${this._isOpen ? 'expand_more' : 'chevron_right'}
                ${css({ position: 'absolute', top: '0', right: '0' })}
                @click=${(e: MouseEvent) => {
                  e.stopPropagation();
                  this._isOpen = !this._isOpen;
                }}
              >
              </x-button>
            `}
      </x-flex>
    `;
  }
}
